////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/*
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
*/
// ##                                                                                                               REQUIREMENTS 'R'
/*
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
R: BitsBound Sales Receptionist Subpea - Individual Message Bubble
- Renders a single chat message in the sales chat interface
- Handles user vs assistant styling with BitsBound brand colors
- Formats message content with full markdown support (tables, lists, code)
- Provides timestamp display
*/

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// DVT_EIS_TP: THIRD-PARTY IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

import React from 'react';
import styled from '@emotion/styled';

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// DVT_IIS: TYPES AND STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// BitsBound Brand Colors
const BRAND = {
  primary: '#6366f1',           // Indigo/purple primary
  primaryLight: '#818cf8',      // Lighter purple
  primaryDark: '#4f46e5',       // Darker purple
  accent: '#22d3ee',            // Cyan accent
  background: '#0f172a',        // Dark slate background
  surface: '#1e293b',           // Elevated surface
  text: '#f8fafc',              // Light text
  textMuted: '#94a3b8',         // Muted text
  border: '#334155',            // Border color
};

const MessageWrapper = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  gap: 4px;
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 85%;
  padding: 14px 18px;
  border-radius: 16px;
  font-size: 0.9375rem;
  line-height: 1.6;
  background: ${props => props.$isUser
    ? `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryDark})`
    : BRAND.surface};
  color: ${props => props.$isUser ? '#ffffff' : BRAND.text};
  border: ${props => props.$isUser ? 'none' : `1px solid ${BRAND.border}`};
  box-shadow: ${props => props.$isUser
    ? '0 4px 15px rgba(99, 102, 241, 0.3)'
    : '0 2px 8px rgba(0, 0, 0, 0.2)'};
  word-break: break-word;

  /* Markdown Styling */
  p {
    margin: 0 0 12px;
    &:last-child { margin-bottom: 0; }
  }

  h1, h2, h3, h4 {
    margin: 16px 0 8px;
    font-weight: 700;
    color: ${props => props.$isUser ? '#ffffff' : BRAND.accent};
    &:first-child { margin-top: 0; }
  }
  h1 { font-size: 1.25rem; }
  h2 { font-size: 1.125rem; }
  h3 { font-size: 1rem; }
  h4 { font-size: 0.9375rem; }

  strong {
    font-weight: 700;
    color: ${props => props.$isUser ? '#ffffff' : BRAND.accent};
  }

  em {
    font-style: italic;
  }

  ul, ol {
    margin: 8px 0 12px;
    padding-left: 24px;
    &:last-child { margin-bottom: 0; }
  }

  li {
    margin-bottom: 6px;
    &:last-child { margin-bottom: 0; }
    &::marker {
      color: ${props => props.$isUser ? '#ffffff' : BRAND.primary};
    }
  }

  code {
    background: ${props => props.$isUser ? 'rgba(0, 0, 0, 0.2)' : 'rgba(99, 102, 241, 0.2)'};
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 0.875em;
  }

  pre {
    background: ${props => props.$isUser ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.3)'};
    padding: 12px 14px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 12px 0;
    &:last-child { margin-bottom: 0; }

    code {
      background: none;
      padding: 0;
    }
  }

  a {
    color: ${props => props.$isUser ? '#ffffff' : BRAND.accent};
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover {
      text-decoration-thickness: 2px;
    }
  }

  blockquote {
    border-left: 3px solid ${props => props.$isUser ? 'rgba(255, 255, 255, 0.4)' : BRAND.primary};
    margin: 12px 0;
    padding-left: 14px;
    font-style: italic;
    opacity: 0.9;
    &:last-child { margin-bottom: 0; }
  }

  hr {
    border: none;
    border-top: 1px solid ${props => props.$isUser ? 'rgba(255, 255, 255, 0.2)' : BRAND.border};
    margin: 16px 0;
  }

  /* Table Styling */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    font-size: 0.875rem;
    &:last-child { margin-bottom: 0; }
  }

  th, td {
    padding: 10px 12px;
    text-align: left;
    border: 1px solid ${props => props.$isUser ? 'rgba(255, 255, 255, 0.2)' : BRAND.border};
  }

  th {
    background: ${props => props.$isUser ? 'rgba(0, 0, 0, 0.15)' : 'rgba(99, 102, 241, 0.15)'};
    font-weight: 600;
    color: ${props => props.$isUser ? '#ffffff' : BRAND.accent};
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.03em;
  }

  td {
    background: ${props => props.$isUser ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.15)'};
  }

  tr:hover td {
    background: ${props => props.$isUser ? 'rgba(0, 0, 0, 0.12)' : 'rgba(99, 102, 241, 0.08)'};
  }

  /* Mobile Responsive */
  @media (max-width: 768px) {
    max-width: 90%;
    padding: 12px 14px;
    font-size: 0.9rem;
    border-radius: 14px;

    h1 { font-size: 1.1rem; }
    h2 { font-size: 1rem; }
    h3 { font-size: 0.9375rem; }
    h4 { font-size: 0.875rem; }

    pre {
      padding: 10px 12px;
      font-size: 0.8rem;
    }

    ul, ol {
      padding-left: 20px;
    }

    table {
      font-size: 0.75rem;
      display: block;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    th, td {
      padding: 8px 10px;
      white-space: nowrap;
    }
  }
`;

const Timestamp = styled.span`
  font-size: 0.6875rem;
  color: ${BRAND.textMuted};
  padding: 0 4px;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.625rem;
  }
`;

/*
████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████
*/
// ##                                                                                                               OUTPUTS 'O'
/*
████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████
*/

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// O_1: MARKDOWN PARSER - XSS-safe markdown to HTML conversion
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

function parseMarkdown(text: string): string {
  // Escape HTML entities first to prevent XSS
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Parse markdown tables BEFORE other processing
  html = html.replace(
    /^(\|[^\n]+\|)\n(\|[-:\s|]+\|)\n((?:\|[^\n]+\|\n?)+)/gm,
    (match, headerRow, separatorRow, bodyRows) => {
      const headers = headerRow
        .split('|')
        .filter((cell: string) => cell.trim())
        .map((cell: string) => `<th>${cell.trim()}</th>`)
        .join('');

      const rows = bodyRows
        .trim()
        .split('\n')
        .map((row: string) => {
          const cells = row
            .split('|')
            .filter((cell: string) => cell.trim())
            .map((cell: string) => `<td>${cell.trim()}</td>`)
            .join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');

      return `<table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
    }
  );

  // Code blocks (```code```)
  html = html.replace(/```(\w*)\n?([\s\S]*?)```/g, '<pre><code>$2</code></pre>');

  // Inline code (`code`)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Headers
  html = html.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Bold
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Italic
  html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
  html = html.replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>');

  // Blockquotes
  html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>');

  // Horizontal rules
  html = html.replace(/^(-{3,}|\*{3,})$/gm, '<hr>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Unordered lists
  html = html.replace(/^[\-\*] (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

  // Ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)(?=<li>)/g, '$1');

  // Paragraphs
  const blocks = html.split(/\n\n+/);
  html = blocks.map(block => {
    const trimmed = block.trim();
    if (!trimmed) return '';
    if (/^<(h[1-4]|ul|ol|pre|blockquote|hr|p|table)/i.test(trimmed)) {
      return trimmed;
    }
    return `<p>${trimmed.replace(/\n/g, '<br>')}</p>`;
  }).join('\n');

  // Clean up double-wrapped paragraphs
  html = html.replace(/<p><p>/g, '<p>').replace(/<\/p><\/p>/g, '</p>');

  return html;
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// O_2: SUBPEA COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

export interface Bitsbound_SalesReceptionistSubpeaProps {
  message: Message;
}

export function Bitsbound_Kings_Home_Primary_SRC_Homepage_SubLevels_SalesReceptionist_Subpea({
  message
}: Bitsbound_SalesReceptionistSubpeaProps): React.ReactElement {
  const isUser = message.role === 'user';

  const formattedTime = new Date(message.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  // Parse markdown for assistant messages, keep user messages as plain text
  const renderedContent = isUser
    ? message.content
    : parseMarkdown(message.content);

  return (
    <MessageWrapper $isUser={isUser}>
      {isUser ? (
        <MessageBubble $isUser={isUser}>
          {message.content}
        </MessageBubble>
      ) : (
        <MessageBubble
          $isUser={isUser}
          dangerouslySetInnerHTML={{ __html: renderedContent }}
        />
      )}
      <Timestamp>{formattedTime}</Timestamp>
    </MessageWrapper>
  );
}

export default Bitsbound_Kings_Home_Primary_SRC_Homepage_SubLevels_SalesReceptionist_Subpea;